import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { type CreateUserFavoriteMutation, UserFavoriteCreateInput } from "@/graphql/generated/graphql";
import { createOperationClient } from "@/services/react-query.service/graphql-client";
import { queryKeys } from "@/services/react-query.service/query-keys";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EVENT_USER_FAVORITE_CREATED, trackUserFavoriteEvent } from "./tracking";
import { createUserFavoriteMutationDocument } from "./user-favorite.gql";

export type CreatedUserFavorite = CreateUserFavoriteMutation["createUserFavorite"];

export interface UseCreateUserFavoriteProps {
  onCompleted?: (createdUserFavorite: CreatedUserFavorite) => void;
  onError?: (error: Error) => void;
}

export const useCreateUserFavorite = ({ onCompleted, onError }: UseCreateUserFavoriteProps = {}) => {
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const queryClient = useQueryClient();

  const {
    mutate: createFavorite,
    isPending: loading,
    error,
  } = useMutation({
    mutationFn: async (input: UserFavoriteCreateInput): Promise<CreateUserFavoriteMutation> => {
      const client = createOperationClient("createUserFavorite");
      return await client.request(createUserFavoriteMutationDocument, { input });
    },
    onSuccess: (data) => {
      const userFavorite = data?.createUserFavorite;
      if (userFavorite) {
        trackUserFavoriteEvent(EVENT_USER_FAVORITE_CREATED, {
          userFavoriteId: userFavorite.id,
        });
      }

      // Invalidate user favorites query
      if (currentWorkspace?.id) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.users.details(), // We'll need to add user favorites query key
        });
      }

      if (onCompleted) {
        onCompleted(data.createUserFavorite);
      }
    },
    onError: (error) => {
      if (onError) {
        onError(error);
      }
    },
  });

  return {
    createFavorite,
    loading,
    error,
  };
};
